import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { HandHeart, MagnifyingGlass, ChatCircleText, Check, X, Sparkle } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SocialPulseBadge from "@/components/SocialPulseBadge";

function MentorCard({ m, onRequest, onMessage }) {
  const s = m.my_request_status;
  return (
    <Card className="flex h-full flex-col border-border/60 p-5 shadow-sm" data-testid={`mentor-card-${m.user.id}`}>
      <div className="flex items-center gap-3">
        <Avatar className="h-11 w-11"><AvatarImage src={m.user.avatar ? fileUrl(m.user.avatar) : undefined} /><AvatarFallback>{(m.user.name || "M")[0]}</AvatarFallback></Avatar>
        <div className="min-w-0">
          <div className="flex items-center gap-1">
            {m.user.slug ? <Link to={`/u/${m.user.slug}`} className="truncate font-heading font-medium hover:underline">{m.user.name}</Link> : <span className="truncate font-heading font-medium">{m.user.name}</span>}
            <SocialPulseBadge verified={m.user.socialpulse_verified} showLabel={false} />
          </div>
          {m.user.headline && <p className="truncate text-xs text-muted-foreground">{m.user.headline}</p>}
        </div>
      </div>
      {m.topics?.length > 0 && <div className="mt-3 flex flex-wrap gap-1.5">{m.topics.map((t) => <Badge key={t} variant="secondary" className="rounded-full font-normal">{t}</Badge>)}</div>}
      {m.bio && <p className="mt-3 line-clamp-3 text-sm text-muted-foreground">{m.bio}</p>}
      <div className="mt-auto pt-4">
        {s === "accepted" ? (
          <Button variant="outline" className="w-full gap-2 rounded-full" onClick={() => onMessage(m.user.id)} data-testid={`mentor-message-${m.user.id}`}><ChatCircleText size={16} /> Message</Button>
        ) : s === "pending" ? (
          <Button variant="outline" disabled className="w-full rounded-full" data-testid={`mentor-requested-${m.user.id}`}>Request sent</Button>
        ) : (
          <Button className="w-full gap-2 rounded-full" onClick={() => onRequest(m)} data-testid={`mentor-request-${m.user.id}`}><HandHeart size={16} /> Request mentorship</Button>
        )}
      </div>
    </Card>
  );
}

export default function Mentorship() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("find");
  const [mentors, setMentors] = useState(null);
  const [q, setQ] = useState("");
  const [me, setMe] = useState(null);
  const [incoming, setIncoming] = useState(null);
  const [outgoing, setOutgoing] = useState(null);
  const [reqFor, setReqFor] = useState(null);
  const [note, setNote] = useState("");

  const loadMentors = useCallback(() => {
    api.get(`/mentorship/mentors?q=${encodeURIComponent(q)}`).then((r) => setMentors(r.data.mentors)).catch(() => setMentors([]));
  }, [q]);
  useEffect(() => { loadMentors(); }, [loadMentors]);
  const loadMe = () => api.get("/mentorship/me").then((r) => setMe(r.data));
  const loadReqs = () => {
    api.get("/mentorship/requests?role=incoming").then((r) => setIncoming(r.data.requests));
    api.get("/mentorship/requests?role=outgoing").then((r) => setOutgoing(r.data.requests));
  };
  useEffect(() => { loadMe(); loadReqs(); }, []);

  const sendRequest = async () => {
    try {
      await api.post("/mentorship/requests", { mentor_id: reqFor.user.id, note, topics: reqFor.topics || [] });
      toast.success("Request sent!");
      setReqFor(null); setNote(""); loadMentors(); loadReqs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send request"); }
  };
  const respond = async (id, action) => {
    try {
      const { data } = await api.post(`/mentorship/requests/${id}/respond`, { action });
      if (action === "accept" && data.conversation_id) { nav(`/messages?c=${data.conversation_id}`); return; }
      toast.success(action === "accept" ? "Accepted" : "Declined");
      loadReqs(); loadMe();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not respond"); }
  };

  const saveProfile = async () => {
    try {
      const payload = { available: me.available, bio: me.bio || "", topics: (me._topicsText ?? (me.topics || []).join(", ")).split(",").map((s) => s.trim()).filter(Boolean) };
      const { data } = await api.put("/mentorship/me", payload);
      setMe({ ...data, pending_incoming: me.pending_incoming });
      toast.success("Mentor profile saved");
      loadMentors();
    } catch (e) { toast.error("Could not save"); }
  };

  return (
    <main className="mx-auto max-w-6xl px-5 py-8" data-testid="mentorship-page">
      <div className="flex items-center gap-2">
        <HandHeart weight="duotone" className="text-secondary" size={30} />
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight">Mentorship</h1>
          <p className="text-sm text-muted-foreground">Give back, or get guidance from fellow alumni.</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList data-testid="mentorship-tabs">
          <TabsTrigger value="find" data-testid="tab-find-mentor">Find a mentor</TabsTrigger>
          <TabsTrigger value="become" data-testid="tab-become-mentor">Become a mentor</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests">
            Requests{me?.pending_incoming ? <span className="ml-1.5 rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">{me.pending_incoming}</span> : null}
          </TabsTrigger>
        </TabsList>

        {/* FIND */}
        <TabsContent value="find" className="mt-5">
          <div className="relative mb-5 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, topic or expertise" className="rounded-full pl-9" data-testid="mentor-search" />
          </div>
          {mentors === null ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="h-44 animate-pulse rounded-2xl bg-muted" />)}</div>
          ) : mentors.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border py-16 text-center">
              <Sparkle size={36} className="mx-auto text-muted-foreground/40" />
              <p className="mt-3 font-medium">No mentors yet</p>
              <p className="mt-1 text-sm text-muted-foreground">Be the first — switch on "Become a mentor".</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="mentors-grid">
              {mentors.map((m) => <MentorCard key={m.user.id} m={m} onRequest={setReqFor} onMessage={async (uid) => { const { data } = await api.post("/dm/start", { user_id: uid }); nav(`/messages?c=${data.id}`); }} />)}
            </div>
          )}
        </TabsContent>

        {/* BECOME */}
        <TabsContent value="become" className="mt-5">
          {me === null ? <div className="h-56 animate-pulse rounded-2xl bg-muted" /> : (
            <Card className="max-w-2xl border-border/60 p-6 shadow-sm">
              <div className="flex items-center justify-between rounded-xl bg-muted/50 p-4">
                <div>
                  <p className="font-medium">Available as a mentor</p>
                  <p className="text-sm text-muted-foreground">Appear in the mentor directory and receive requests.</p>
                </div>
                <Switch checked={!!me.available} onCheckedChange={(v) => setMe((p) => ({ ...p, available: v }))} data-testid="mentor-available-toggle" />
              </div>
              <div className="mt-5 space-y-1.5">
                <Label>Areas / topics you can help with</Label>
                <Input defaultValue={(me.topics || []).join(", ")} onChange={(e) => setMe((p) => ({ ...p, _topicsText: e.target.value }))} placeholder="Product Management, Study Abroad, UPSC prep" data-testid="mentor-topics" />
                <p className="text-xs text-muted-foreground">Separate topics with commas.</p>
              </div>
              <div className="mt-4 space-y-1.5">
                <Label>A short note for mentees</Label>
                <Textarea value={me.bio || ""} onChange={(e) => setMe((p) => ({ ...p, bio: e.target.value }))} rows={4} placeholder="How you like to help, your background, availability…" data-testid="mentor-bio" />
              </div>
              <Button className="mt-5 rounded-full" onClick={saveProfile} data-testid="mentor-save">Save mentor profile</Button>
            </Card>
          )}
        </TabsContent>

        {/* REQUESTS */}
        <TabsContent value="requests" className="mt-5 space-y-8">
          <div>
            <h2 className="mb-3 font-heading text-lg font-medium">Requests to me</h2>
            {incoming === null ? <div className="h-20 animate-pulse rounded-2xl bg-muted" /> : incoming.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">No mentorship requests yet.</p>
            ) : (
              <div className="space-y-3" data-testid="incoming-requests">
                {incoming.map((r) => (
                  <Card key={r.id} className="flex flex-wrap items-center justify-between gap-3 border-border/60 p-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10"><AvatarImage src={r.other.avatar ? fileUrl(r.other.avatar) : undefined} /><AvatarFallback>{(r.other.name || "M")[0]}</AvatarFallback></Avatar>
                      <div>
                        <p className="font-medium">{r.other.name}</p>
                        {r.note && <p className="text-sm text-muted-foreground">"{r.note}"</p>}
                        {r.topics?.length > 0 && <div className="mt-1 flex flex-wrap gap-1">{r.topics.map((t) => <Badge key={t} variant="outline" className="rounded-full text-xs font-normal">{t}</Badge>)}</div>}
                      </div>
                    </div>
                    {r.status === "pending" ? (
                      <div className="flex gap-2">
                        <Button size="sm" className="gap-1 rounded-full" onClick={() => respond(r.id, "accept")} data-testid={`req-accept-${r.id}`}><Check size={15} /> Accept</Button>
                        <Button size="sm" variant="outline" className="gap-1 rounded-full" onClick={() => respond(r.id, "decline")} data-testid={`req-decline-${r.id}`}><X size={15} /> Decline</Button>
                      </div>
                    ) : r.status === "accepted" ? (
                      <Button size="sm" variant="outline" className="gap-1 rounded-full" onClick={() => nav(`/messages?c=${r.conversation_id}`)}><ChatCircleText size={15} /> Message</Button>
                    ) : <Badge variant="secondary" className="capitalize">{r.status}</Badge>}
                  </Card>
                ))}
              </div>
            )}
          </div>
          <div>
            <h2 className="mb-3 font-heading text-lg font-medium">My requests</h2>
            {outgoing === null ? <div className="h-20 animate-pulse rounded-2xl bg-muted" /> : outgoing.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">You haven't requested a mentor yet.</p>
            ) : (
              <div className="space-y-3" data-testid="outgoing-requests">
                {outgoing.map((r) => (
                  <Card key={r.id} className="flex items-center justify-between gap-3 border-border/60 p-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10"><AvatarImage src={r.other.avatar ? fileUrl(r.other.avatar) : undefined} /><AvatarFallback>{(r.other.name || "M")[0]}</AvatarFallback></Avatar>
                      <p className="font-medium">{r.other.name}</p>
                    </div>
                    {r.status === "accepted" ? (
                      <Button size="sm" variant="outline" className="gap-1 rounded-full" onClick={() => nav(`/messages?c=${r.conversation_id}`)}><ChatCircleText size={15} /> Message</Button>
                    ) : <Badge variant={r.status === "declined" ? "secondary" : "outline"} className="capitalize">{r.status}</Badge>}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Request dialog */}
      <Dialog open={!!reqFor} onOpenChange={(o) => { if (!o) { setReqFor(null); setNote(""); } }}>
        <DialogContent data-testid="mentor-request-dialog">
          <DialogHeader><DialogTitle className="font-heading text-lg">Request {reqFor?.user.name} as a mentor</DialogTitle></DialogHeader>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Introduce yourself and what you'd like help with…" data-testid="mentor-request-note" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => { setReqFor(null); setNote(""); }}>Cancel</Button>
            <Button className="rounded-full" onClick={sendRequest} data-testid="mentor-request-send">Send request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
